import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";
import {
  IconLayoutDashboard,
  IconSend,
  IconBolt,
  IconEye,
  IconTrophy,
  IconChartDonut2,
  IconBell,
  IconUserCheck,
  IconBriefcase,
} from "@tabler/icons-react";
import classes from "./dashboard.module.css";
import { UserContext } from "../../../context";
import {
  talentActivitySummary,
  getNotifications,
  markNotificationAsRead,
} from "../../../services/talentCalls";
import DoughnutChart from "../../../components/analytics/analytics";
import { errorHandler } from "../../../utils";
import { DocumentHeader } from "../../../components/Seo";

const NOTIF_PAGE_SIZE = 5;

const LEGEND = [
  { key: "jobsApplied", label: "Jobs Applied", color: "#1d4ed8" },
  { key: "jobsAccepted", label: "Jobs Accepted", color: "#047857" },
  { key: "activeJobs", label: "Active Jobs", color: "#84cc16" },
  { key: "rejectedJobs", label: "Rejected Jobs", color: "#dc2626" },
  { key: "jobsHired", label: "Jobs Hired", color: "#7dd3fc" },
  { key: "jobsViewed", label: "Jobs Viewed", color: "#d97706" },
  { key: "completedJobs", label: "Completed Jobs", color: "#b57edc" },
];

export const getNotificationContent = (notification) => {
  const { type, title, message, relatedId } = notification;
  switch (type) {
    case "JOB_ALERT": {
      const jobTitle = title?.replace("New Job Alert: ", "") || "Job";
      return {
        icon: <IconBriefcase size={18} />,
        iconBg: "#fef3c7",
        iconColor: "#d97706",
        title,
        body: `Hola! A new ${jobTitle} job with your skills just got posted.`,
        link: `/job/${relatedId || ""}`,
        showLink: true,
      };
    }
    case "ONBOARDING_REMINDER":
      return {
        icon: <IconUserCheck size={18} />,
        iconBg: "#eff2ff",
        iconColor: "#1d4ed8",
        title: "Onboarding Reminder",
        body: "Just a reminder that you haven't completed onboarding.",
        link: "/talent-onboarding?stage=1",
        showLink: true,
      };
    case "VERIFICATION_SUCCESS":
      return {
        icon: <IconUserCheck size={18} />,
        iconBg: "#eff2ff",
        iconColor: "#1d4ed8",
        title: "Verification Success",
        body: "Nice! You just got verified! You are now ready to start applying for jobs.",
        link: "/job-feed",
        showLink: true,
      };
    case "APPLICATION_ACCEPTED":
      return {
        icon: <IconBriefcase size={18} />,
        iconBg: "#fef3c7",
        iconColor: "#d97706",
        title: title || "Application Accepted",
        body: message?.replace(/<[^>]*>/g, "") || "A client just accepted your application. Congratulations!",
        link: "/matches",
        showLink: true,
      };
    case "SKILL_DEVELOPMENT":
      return {
        icon: <IconUserCheck size={18} />,
        iconBg: "#eff2ff",
        iconColor: "#1d4ed8",
        title: "Skill Development",
        body: "Check out our recommended self development resources.",
        link: "/self-development",
        showLink: true,
      };
    case "PROFILE_UPDATED":
    default:
      return {
        icon: <IconUserCheck size={18} />,
        iconBg: "#eff2ff",
        iconColor: "#1d4ed8",
        title: title || "Profile Updated",
        body: message?.replace(/<[^>]*>/g, "") || "You have just updated your profile.",
        showLink: false,
      };
  }
};

const Dashboard = () => {
  const { data } = useContext(UserContext);
  const navigate = useNavigate();

  const [metrics, setMetrics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifTotal, setNotifTotal] = useState(0);
  const [notifPage, setNotifPage] = useState(1);
  const [load, setLoad] = useState(false);
  const [donutSize, setDonutSize] = useState(window.innerWidth <= 480 ? 130 : 160);

  useEffect(() => {
    const handleResize = () => setDonutSize(window.innerWidth <= 480 ? 130 : 160);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoad(true);
      try {
        const res = await talentActivitySummary();
        if (res?.data?.data?.metrics) {
          setMetrics(res?.data?.data?.metrics);
        }
      } catch (error) {
        error?.response?.data
          ? errorHandler(error?.response?.data?.errors, "Error")
          : errorHandler(
              "Network error: Something went wrong, Please check your internet connection and try again.",
              "Error",
            );
      } finally {
        setLoad(false);
      }
    };

    fetchSummary();
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await getNotifications(notifPage, NOTIF_PAGE_SIZE);
        setNotifications(res?.data?.data?.notifications || []);
        setNotifTotal(res?.data?.data?.total || 0);
      } catch (_) {}
    };
    fetchNotifications();
  }, [notifPage]);

  const handleNotificationClick = async (notification, link) => {
    if (!notification.isRead) {
      try {
        await markNotificationAsRead(notification._id);
        setNotifications((prev) =>
          prev.map((n) => (n._id === notification._id ? { ...n, isRead: true } : n)),
        );
      } catch (_) {}
    }
    if (link) navigate(link);
  };

  const checklist = [
    { label: "Primary role", done: !!data?.pryRole },
    { label: "Work history", done: (data?.workHistoryExperience?.length || 0) > 0 },
    { label: "Education", done: (data?.educations?.length || 0) > 0 },
    { label: "Skills & abilities", done: !!data?.abilities },
    { label: "Psychometric test", done: !!data?.submittedPsychometricTest },
  ];
  const completedCount = checklist.filter((c) => c.done).length;
  const profilePct = Math.round((completedCount / checklist.length) * 100) || 0;

  const legendValues = LEGEND.map((item) => metrics?.[item.key] || 0);
  const hasData = legendValues.some((v) => v > 0);

  const quickStats = [
    { key: "jobsApplied", label: "Jobs Applied", icon: <IconSend size={18} />, tone: "blue" },
    { key: "activeJobs", label: "Active Jobs", icon: <IconBolt size={18} />, tone: "green" },
    { key: "jobsViewed", label: "Jobs Viewed", icon: <IconEye size={18} />, tone: "amber" },
    { key: "jobsHired", label: "Jobs Hired", icon: <IconTrophy size={18} />, tone: "violet" },
  ];

  return (
    <div className={classes.main}>
      <DocumentHeader title="Awasource | Talent Dashboard" />

      <div className={classes.pageHeader}>
        <span className={classes.eyebrow}>
          <IconLayoutDashboard size={14} /> Dashboard
        </span>
        <h1 className={classes.pageTitle}>Welcome back{data?.firstName ? `, ${data.firstName}` : ""}.</h1>
        <p className={classes.pageSubtitle}>
          Here's a snapshot of your job search activity and the latest updates on your profile.
        </p>
      </div>

      <div className={classes.statShelf}>
        {quickStats.map((stat) => (
          <div key={stat.key} className={`${classes.statCard} ${classes[`stat${stat.tone.charAt(0).toUpperCase()}${stat.tone.slice(1)}`]}`}>
            <div className={classes.statIcon}>{stat.icon}</div>
            <div className={classes.statValue}>{load ? "–" : metrics?.[stat.key] || 0}</div>
            <div className={classes.statLabel}>{stat.label}</div>
          </div>
        ))}
      </div>

      <div className={classes.dashRow}>
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div className={classes.cardHeaderLeft}>
              <div className={classes.cardIcon}>
                <IconChartDonut2 size={18} />
              </div>
              <div>
                <div className={classes.cardTitle}>Activity Summary</div>
                <div className={classes.cardSubtitle}>Your job search funnel, updated in real time</div>
              </div>
            </div>
          </div>
          <div className={`${classes.cardBody} ${classes.activityBody}`}>
            <div className={classes.donutWrap}>
              <DoughnutChart
                width={donutSize}
                height={donutSize}
                labels={LEGEND.map((l) => l.label)}
                values={legendValues}
                colors={LEGEND.map((l) => l.color)}
                showLegend={false}
              />
              {!hasData && (
                <div className={classes.donutHole}>
                  <IconChartDonut2 size={20} />
                  <span className={classes.donutLabel}>No Data<br />Available</span>
                </div>
              )}
            </div>
            <div className={classes.legend}>
              {LEGEND.map((item, i) => (
                <div key={item.key} className={classes.legendItem}>
                  <span className={classes.legendItemLeft}>
                    <span className={classes.legendDot} style={{ background: item.color }} />
                    {item.label}
                  </span>
                  <span className={classes.legendValue}>{legendValues[i]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={`${classes.completionCard} ${classes.ctaCard}`}>
          <div className={classes.completionHeader}>
            <span className={classes.completionTitle}>Profile strength</span>
            <span className={classes.completionPct}>{profilePct}%</span>
          </div>
          <div className={classes.progressBar}>
            <div className={classes.progressFill} style={{ width: `${profilePct}%` }} />
          </div>
          <p className={classes.completionHint}>
            A complete profile gets up to 3x more recruiter views. Add your skills and portfolio to boost your match score.
          </p>
          <span className={classes.btnComplete} onClick={() => navigate("/talent-profile")}>
            <IconUserCheck size={15} /> Complete your profile
          </span>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardHeader}>
          <div className={classes.cardHeaderLeft}>
            <div className={classes.cardIcon}>
              <IconBell size={18} />
            </div>
            <div>
              <div className={classes.cardTitle}>Notifications</div>
              <div className={classes.cardSubtitle}>
                {notifications.filter((n) => !n.isRead).length} unread alerts on your profile and job matches
              </div>
            </div>
          </div>
        </div>
        <div className={`${classes.cardBody} ${classes.notifList}`}>
          {notifications.length === 0 ? (
            <p className={classes.emptyState}>No notifications yet.</p>
          ) : (
            notifications.map((notification) => {
              const content = getNotificationContent(notification);
              return (
                <div key={notification._id} className={classes.notifRow}>
                  <div className={classes.notifRowMain}>
                    <div
                      className={classes.notifIconWrap}
                      style={{ background: content.iconBg, color: content.iconColor }}
                    >
                      {content.icon}
                    </div>
                    <div className={classes.notifRowContent}>
                      <div className={classes.notifRowTitle}>{content.title}</div>
                      <div className={classes.notifRowBody}>{content.body}</div>
                      <div className={classes.notifRowTime}>
                        {new Date(notification.createdAt).toLocaleString([], {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  </div>
                  {!notification.isRead && (
                    <div className={classes.notifRowSide}>
                      <span className={classes.unreadBadge}>Unread</span>
                      {content.showLink && (
                        <span
                          className={classes.viewLink}
                          onClick={() => handleNotificationClick(notification, content.link)}
                        >
                          View here
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {notifTotal > NOTIF_PAGE_SIZE && (
            <div className={classes.paginationWrap}>
              <Pagination
                pageSize={NOTIF_PAGE_SIZE}
                current={notifPage}
                total={notifTotal}
                onChange={(p) => setNotifPage(p)}
                nextIcon={<span>&rsaquo;</span>}
                prevIcon={<span>&lsaquo;</span>}
                showTitle={false}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
